import { ChangeEvent } from "react";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { LifeStageDateStatusEnum } from "@/Common/Models/LifeStageDateStatusEnum";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { Dispatcher } from "@/Common/Stores/Dispatcher";

interface ITextboxProps {
    id?: string;
    dataCy?: string;
    operation?: number;
    onChange?: (textOrDate: any) => void;
    onFocus?: (e: React.FocusEvent<HTMLDivElement>) => void;
    onBlur?: (e: any, textOrDate?: any) => void;
    onChangeOrBlur?: (textOrDate?: any) => void;
    value?: string;
    placeholder?: string;
    // TODO: change type for HTMLInputTypeAttribute when migration to React 17 is done.
    type?: "button" | "checkbox" | "color" | "date" | "datetime-local" | "email" | "file" | "hidden" | "image" | "month" | "number" | "password" | "radio" | "range" | "reset" | "search" | "submit" | "tel" | "text" | "time" | "url" | "week";
    item?: any;
    className?: string;
    containerCssClass?: string;
    RemoveStyle?: boolean;
    IsSmall?: boolean;
    tabIndex?: number;

    debounceMs?: number;

    style?: React.CSSProperties;
    noAutoCompleteStyle?: React.CSSProperties;

    IsNumeric?: boolean;
    isInteger?: boolean;
    min?: number;
    max?: number;
    IsDate?: boolean;
    ExpectedDatePrecisionMinimum?: LifeStageDatePrecisionEnum;
    disabled?: boolean;
    disabledClass?: string;

    autoCompleteItems?: Immutable.List<SearchableItemModelT<any>>;
    onAutoCompleteSelectCallBack?: (Code: string, Model: any, Value: string) => void;
    onAutoCompleteSelectCallBackOperation?: number;
    onAutoCompleteSelectClearTextBox?: boolean;
    onAutoCompleteSelectDoNotUpdateTextBox?: boolean;
    onClickRestrictedSearch?: boolean;
    limit?: number;
    disableMatchAutoSorting?: boolean;

    operationArg1?: any;
    operationArg2?: any;
    operationArg3?: any;

    valid?: boolean;
    onMountFocus?: boolean;
    maxDecimal?: number;
    maxLength?: number;
    autoComplete?: string;
    name?: string;
    focus?: boolean;
    listMaxHeight?: string;
    listWidth?: string;
    flexGrow?: number;
    textboxMaxWidth?: string;
    refocusAfterSelect?: boolean;
    reversePopup?: boolean;

    onSearchCallback?: (text: string) => Promise<SearchableItemModelT<any>[]>;
    onKeyDown?: (e: React.KeyboardEvent) => void;
}

class TextboxControlState {
    public autoCompleteItemsClean: Immutable.List<SearchableItemModelT<any>>;
    public autoCompleteItemsMatches: Immutable.List<SearchableItemModelT<any>>;
    public text: string = "";
    public precision: LifeStageDatePrecisionEnum;
    public datePrecision: DateTimeWithPrecision;
    public forceHideDropDown: boolean;
    public preventOnBlurFromDropdownClick: boolean;

    constructor(init?: Partial<TextboxControlState>) {
        Object.assign(this, init);
    }

    public static default(): TextboxControlState {
        return new TextboxControlState({
            text: "",
            forceHideDropDown: false,
            autoCompleteItemsMatches: Immutable.List([]),
            autoCompleteItemsClean: Immutable.List([])
        });
    }
}

class TextboxControl extends BaseReactComponent<ITextboxProps, TextboxControlState> {
    private myleHelper = sessionStore.helper;
    private id: string;
    private isInFocus = false;
    private isMouseIn = false;
    private isTouching = false;
    private timeoutIsClosing;
    private defaultItemLimitCount: number = 12;
    private inputRef: HTMLInputElement = null;
    private readonly UnlimitedMaxChar = 5000;

    constructor(props: ITextboxProps) {
        super(props);

        const initialState = TextboxControlState.default();
        initialState.text = this.props.value ?? "";
        initialState.forceHideDropDown = true;
        initialState.autoCompleteItemsClean = this.props.autoCompleteItems ?? Immutable.List([]);
        this.state = initialState;
    }

    public render(): JSX.Element {
        this.id = this.props.id;
        if (this.id == null || this.id == "")
            this.id = GuidHelper.NewGuid();

        const css: React.CSSProperties = { ...this.props.style };

        if (this.props.IsSmall)
            css.height = "30px";

        if (this.props.IsDate && this.state.datePrecision != null) {
            switch (this.state.datePrecision.status) {
                case LifeStageDateStatusEnum.Valid: css.backgroundColor = "#c9ffde"; break;
                case LifeStageDateStatusEnum.ValidWithExtraChar: css.backgroundColor = "lightyellow"; break;
                case LifeStageDateStatusEnum.Invalid: css.backgroundColor = "#ffccd6"; break;
                default: break;
            }
        }

        // Old TextBoxControl
        if ((this.props.autoCompleteItems == null && !this.props.onSearchCallback) || this.props.disabled) {
            let className = this.props.className ?? "form-control input";

            if (this.props.disabled && IsNullOrUndefined(css.height))
                css.height = "37px";

            return (
                <div style={this.props.noAutoCompleteStyle} className={this.props.containerCssClass}>
                    <input
                        style={css}
                        disabled={this.props.disabled}
                        autoFocus={!!this.props.focus}
                        className={this.props.valid === false ? className + " invalid" : className}
                        id={this.id}
                        data-cy={this.props.dataCy}
                        onBlur={e => this.onBlur(e)}
                        type={this.props.type ?? "text"}
                        value={this.state.text}
                        placeholder={this.props.placeholder}
                        onChange={e => this.onTextChange(e)}
                        required
                        onFocus={e => this.onFocus(e)}
                        onKeyDown={e => this.oldTextboxKeyDown(e)}
                        maxLength={this.props.maxLength ?? this.UnlimitedMaxChar}
                        autoComplete={this.props.autoComplete}
                        name={this.props.name}
                        ref={inputRef => this.inputRef = inputRef}
                        tabIndex={this.props.tabIndex}
                        min={this.props.min ?? Number.MIN_SAFE_INTEGER}
                        max={this.props.max ?? Number.MAX_SAFE_INTEGER}
                    />
                </div>
            );
        }

        let parentClassName = this.props.valid === false ? "patient-autocomplete invalid" : "patient-autocomplete";
        if (this.props.disabled)
            parentClassName += " disabled";
        let subParentClassName = "patient-search-field";
        let subParentStyle: React.CSSProperties = { height: "37px", paddingTop: "8px" };

        if (this.props.RemoveStyle) {
            parentClassName = "";
            subParentClassName = "";
            subParentStyle = {};
        }

        let listMaxHeight = this.props.listMaxHeight ?? "auto";

        let parentdivStyle: React.CSSProperties = { position: "relative" };
        if (this.props.textboxMaxWidth != null)
            parentdivStyle.maxWidth = this.props.textboxMaxWidth;

        if (this.props.flexGrow > 0)
            parentdivStyle.flexGrow = this.props.flexGrow;

        let listStyle: React.CSSProperties = { zIndex: 2000, position: "absolute", top: subParentStyle.height, left: "0px", maxHeight: listMaxHeight, width: "auto", minWidth: "200px" };
        if (this.props.listWidth) {
            listStyle.width = this.props.listWidth;
            listStyle.minWidth = "";
        }

        if (!!this.props.reversePopup) {
            listStyle.top = null;
            listStyle.bottom = subParentStyle.height;
        }

        return (
            <div className={parentClassName} style={parentdivStyle}>
                <div className={subParentClassName} style={subParentStyle}>
                    <input
                        disabled={this.props.disabled}
                        style={css}
                        className={this.props.valid === false ? this.props.className + " invalid" : this.props.className}
                        id={this.id}
                        data-cy={this.props.dataCy}
                        type={this.props.type ?? "text"}
                        value={this.state.text}
                        placeholder={this.props.placeholder}
                        onChange={e => this.onTextChange(e)}
                        onClick={() => this.onClick()}
                        onFocus={e => this.onFocus(e)}
                        onBlur={e => !this.state.preventOnBlurFromDropdownClick && this.onBlur(e)}
                        onKeyDown={e => this.inputKeyDown(e)}
                        maxLength={this.props.maxLength ?? this.UnlimitedMaxChar}
                        autoComplete={this.props.autoComplete}
                        name={this.props.name}
                        ref={inputRef => this.inputRef = inputRef}
                        tabIndex={this.props.tabIndex}
                        min={this.props.min ?? Number.MIN_SAFE_INTEGER}
                        max={this.props.max ?? Number.MAX_SAFE_INTEGER}
                    />
                </div>

                {(this.props.autoCompleteItems || this.props.onSearchCallback)
                    && this.state.autoCompleteItemsMatches?.count() > 0
                    && !this.state.forceHideDropDown
                    && (
                        <div
                            id={this.id + "list-container"}
                            className="list-container-txtbox"
                            onMouseEnter={() => this.onMouseEnter()}
                            onMouseLeave={() => this.onMouseLeave()}
                            onTouchStart={() => this.onTouchStart()}
                            onTouchEnd={() => this.onTouchEnd()}
                            style={listStyle}
                        >
                            <div className="results-container" onClick={e => e.stopPropagation()}>
                                <ul className="results-list" data-cy={this.props.dataCy ? `${this.props.dataCy}-list` : null}>
                                    {this.state.autoCompleteItemsMatches.map((item: SearchableItemModelT<any>, i: number) => (
                                        <li key={i + "_code_"}>
                                            <div
                                                className={"flex-container " + (item.Active ? "active" : "")}
                                                style={item.Active ? { "backgroundColor": "#f4f6f9" } : {}}
                                                onKeyDown={e => this.inputKeyDown(e)}
                                                onMouseDown={() => this.mutateState(s => s.preventOnBlurFromDropdownClick = true)}
                                                onClick={() => this.selectItem(item)}
                                                data-cy={`result_item_${item.Code}`}
                                            >
                                                <a>{item.Value}</a>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )
                }
            </div>
        );
    }

    public componentWillUpdate(nextProps: ITextboxProps): void {
        if (nextProps.value !== this.props.value || nextProps.autoCompleteItems !== this.props.autoCompleteItems) {
            this.mutateState((x: TextboxControlState) => {
                x.text = nextProps.value ?? "";
                x.autoCompleteItemsClean = nextProps.autoCompleteItems ?? Immutable.List([]);
            });
        }
    }

    public componentDidMount(): void {
        window.setTimeout(() => {
            if (this.props.onMountFocus === true) {
                $("#" + this.id).focus();
                $("#" + this.id).select();
            }
        }, 200);

        if (this.props.autoCompleteItems)
            window.addEventListener("blur", () => this.inputRef?.blur()); // prevent triggering the focus event and showing dropdown when refocusing in Myle window
    }

    public componentWillUnmount(): void {
        if (this.timeoutIsClosing != null)
            clearTimeout(this.timeoutIsClosing);

        window.removeEventListener("blur", () => this.inputRef?.blur());
    }

    private updateAutocompleteMatches(matches: SearchableItemModelT<any>[]): void {
        this.mutateState(x => {
            x.forceHideDropDown = false;
            x.autoCompleteItemsMatches = Immutable.List(matches);
        });
    }

    private onTextChange(e: ChangeEvent<HTMLInputElement>): void {
        let newText = e.target.value;
        this.textChange(newText, true);
    }

    private onClick(): void {
        if (!this.isInFocus) {
            if (this.props.autoCompleteItems?.count()) {
                this.mutateState(state => {
                    state.forceHideDropDown = !this.state.forceHideDropDown; // Toggle

                    if (!state.forceHideDropDown) {
                    // Toggle on
                        let SearchText = (this.props.onClickRestrictedSearch == true) ? this.state.text : "";
                        state.autoCompleteItemsMatches = this.getMatches(SearchText).toImmutable();
                    }
                });
            }
            else if (this.props.onSearchCallback && this.state.autoCompleteItemsMatches?.count()) {
                this.mutateState(state => state.forceHideDropDown = !this.state.forceHideDropDown);
            }
        }
    }

    private onFocus(e: React.FocusEvent<HTMLInputElement>): void {
        this.isInFocus = true;
        if (this.props.autoCompleteItems?.count()) {
            this.mutateState(state => {
                state.forceHideDropDown = false;

                if (!state.forceHideDropDown) {
                    const searchText = this.props.onClickRestrictedSearch ? state.text : "";
                    state.autoCompleteItemsMatches = this.getMatches(searchText).toImmutable();
                }
            });
        }
        if (this.props.onFocus != null) {
            this.props.onFocus(e);
        }
    }

    private onBlur(e: React.FocusEvent<HTMLInputElement>): void {
        this.props.onBlur?.(e, e.target.value);

        if (e.target.value != null)
            this.props.onChangeOrBlur?.(e.target.value);

        this.isInFocus = false;
        this.tryCloseDropdown();
    }

    public tryCloseDropdown(): void {
        if (this.timeoutIsClosing !== -1) {
            clearTimeout(this.timeoutIsClosing);
            this.timeoutIsClosing = -1;
        }

        this.timeoutIsClosing = window.setTimeout(() => {
            if (!this.isInFocus && !this.isMouseIn && !this.isTouching)
                this.mutateState(state => {
                    state.autoCompleteItemsMatches = [].toImmutable();
                    state.forceHideDropDown = true;
                });
        }, 200);
    }

    public onMouseEnter(): void {
        this.isMouseIn = true;
    }

    public onTouchStart(): void {
        this.isTouching = true;
    }

    public onTouchEnd(): void {
        this.isTouching = false;
        this.tryCloseDropdown();
    }

    public onMouseLeave(): void {
        this.isMouseIn = false;
        this.tryCloseDropdown();
    }

    public focus(): void {
        this.inputRef.focus();
    }

    private textChange(newText: string, Search: boolean): void {
        if (this.props.maxLength !== undefined && newText.length > this.props.maxLength) {
            newText = newText.slice(0, this.props.maxLength);
        }

        let matches: SearchableItemModelT<any>[] = [];
        if (this.props.autoCompleteItems?.count() && Search) {
            matches = this.getMatches(newText);
        }

        let safeDebounceMs = this.props.debounceMs;
        if (safeDebounceMs == null)
            safeDebounceMs = 5;

        if (this.props.isInteger)
            newText = this.filterAsInteger(newText);
        else if (this.props.IsNumeric)
            newText = MyleHelper.filterStringAsDecimal(newText);

        if ((this.props.type === "number" || this.props.IsNumeric || this.props.isInteger)
            && !IsNullOrWhiteSpace(newText)
            && (
                (Number(newText) < (this.props.min ?? Number.MIN_SAFE_INTEGER))
                || (Number(newText) > (this.props.max ?? Number.MAX_SAFE_INTEGER))
            )
        ) {
            return;
        }

        let dateWithPrecision: DateTimeWithPrecision = null;
        if (this.props.IsDate) {
            dateWithPrecision = new DateTimeWithPrecision(newText, this.state.text, this.props.ExpectedDatePrecisionMinimum);
            newText = dateWithPrecision.text;
        }

        if (newText === this.state.text)
            return;

        this.mutateState(
            state => {
                if (this.props.IsDate) {
                    state.datePrecision = dateWithPrecision;
                }

                state.text = newText;
                state.autoCompleteItemsMatches = Immutable.List(matches);
                state.forceHideDropDown = false;
            },
            () => {
                window.setTimeout(
                    () => {
                        if (newText != this.state.text) {
                            return; /* Wait for next debounce */
                        }

                        Dispatcher.dispatch(
                            this.props.operation,
                            {
                                item: this.props.item,
                                value: newText,
                                id: this.props.id,
                                arg1: this.props.operationArg1,
                                arg2: this.props.operationArg2,
                                arg3: this.props.operationArg3
                            }
                        );

                        if (this.props.IsDate) {
                            this.props.onChange?.(dateWithPrecision);
                            this.props.onChangeOrBlur?.(dateWithPrecision);
                        }
                        else {
                            this.props.onChange?.(newText);
                            this.props.onChangeOrBlur?.(newText);
                        }

                        if (this.props.onSearchCallback && !this.props.autoCompleteItems)
                            this.onBackendSearch(newText);
                    },
                    this.props.onSearchCallback ? this.props.debounceMs ?? 200 : safeDebounceMs
                );
            }
        );
    }

    private async onBackendSearch(text: string): Promise<void> {
        // Call backend request handler and show list.
        const list = await this.props.onSearchCallback(text);
        this.mutateState(s => {
            s.autoCompleteItemsClean = list.toImmutable();
            s.autoCompleteItemsMatches = list.toImmutable();
        });
    }

    private getMatches(searchText: string): SearchableItemModelT<any>[] {
        let matches: SearchableItemModelT<any>[] = [];

        let cleanText: string = this.myleHelper.RemoveDiacritics(searchText).toLowerCase();
        let splittedSearch = cleanText.split(" ");

        const conditions: ((value: string) => boolean)[] = [
            () => false,
            () => true,
            () => true
        ];

        if (splittedSearch?.length > 0)
            conditions[0] = (value: string) => value?.split(" ").some(word => word.contains(splittedSearch[0]));

        if (splittedSearch?.length > 1)
            conditions[1] = (value: string) => value?.contains(splittedSearch[1]);

        if (splittedSearch?.length > 2)
            conditions[2] = (value: string) => value?.contains(splittedSearch[2]);

        matches = this.state.autoCompleteItemsClean
            .filter(x => conditions.all(condition => condition(x.Value_Searchable)))
            .toArray();

        if (!this.props.disableMatchAutoSorting)
            matches = ArrayHelper.SortDynamic(matches, "Value");

        const limit = typeof this.props.limit === "undefined"
            ? this.defaultItemLimitCount
            : this.props.limit;

        if (limit > 0)
            matches = matches.slice(0, limit);

        return matches;
    }

    private inputKeyDown(e: React.KeyboardEvent): void {
        // up
        if (e.keyCode === 38) { e.preventDefault(); this.moveSelectionUpOrDown(true); }

        // down
        if (e.keyCode === 40) { e.preventDefault(); this.moveSelectionUpOrDown(false); }

        // enter
        if (e.keyCode === 13) {
            if (!this.state.forceHideDropDown == true && this.state.autoCompleteItemsMatches != null && this.state.autoCompleteItemsMatches.count() != 0) {
                this.addSeletedItem();
            }
        }

        // escape
        if (e.keyCode === 27 || e.keyCode === 9) {
            this.mutateState(s => s.forceHideDropDown = true);
        }

        this.props.onKeyDown?.(e);
    }

    // only acceptable key code(for now) for the vital sign height and weight
    // NON NUMPAD KEYS:
    // 48 - 57 = 0 - 9 || 190 = .
    // ---------------------------
    // NUMPAD KEYS
    // 110 = . || 96 - 105 = 0 - 9
    private keyArray = [48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 190, 110, 96, 97, 98, 99, 100, 101, 102, 103, 104, 105];

    private oldTextboxKeyDown(e: React.KeyboardEvent): void {
        if (this.keyArray.some(c => c === e.keyCode)) {
            if (this.props.maxDecimal != null) {
                if (this.state.text.indexOf(".") != -1 && (this.state.text.length - this.state.text.indexOf(".") - 1) >= 3) {
                    e.preventDefault();
                }
            }
        }
        this.props.onKeyDown?.(e);
    }

    // keyboard triggered
    private moveSelectionUpOrDown(IsDirectionUp: boolean): void {
        let collection = this.state.autoCompleteItemsMatches.toArray();
        if (collection.length == 0)
            return;

        let idx = this.indexOfSelectedItem();
        if (idx === -1) {
            collection.forEach(x => x.Active = false);
            collection[IsDirectionUp ? collection.length - 1 : 0].Active = true;
            this.updateAutocompleteMatches(collection);
            return;
        }

        if ((idx === 0 && IsDirectionUp) || (idx === collection.length - 1 && !IsDirectionUp)) {
            // collection[IsDirectionUp ? 0 : collection.length - 1].Active = false;
            // this.UpdateAutocompleteMatches(collection);
            return;
        }

        collection[idx].Active = false;
        idx = idx + (IsDirectionUp ? -1 : 1);

        collection[idx].Active = true;
        this.updateAutocompleteMatches(collection);
    }

    private indexOfSelectedItem(): number {
        if (this.state.autoCompleteItemsMatches.count() == 0)
            return -1;

        for (let i = 0; i < this.state.autoCompleteItemsMatches.count(); i++) {
            let item = this.state.autoCompleteItemsMatches.get(i);
            if (item.Active == true) {
                return i;
            }
        }
        return -1;
    }

    private selectItem(item: SearchableItemModelT<any>): void {
        this.mutateState(
            s => {
                s.autoCompleteItemsMatches = Immutable.List([]);
                s.forceHideDropDown = true;
                s.preventOnBlurFromDropdownClick = false;

                if (this.props.onAutoCompleteSelectDoNotUpdateTextBox != true)
                    s.text = this.props.onAutoCompleteSelectClearTextBox ? "" : item.Value;
            },
            () => {
                if (item != null && this.props.onAutoCompleteSelectCallBack != null)
                    this.props.onAutoCompleteSelectCallBack(item.Code, item.Model, item.Value);

                if (item != null && this.props.onAutoCompleteSelectCallBackOperation != null) {
                    Dispatcher.dispatch(
                        this.props.onAutoCompleteSelectCallBackOperation,
                        {
                            item: item.Model,
                            value: item.Value
                        }
                    );
                }
                if (this.props.refocusAfterSelect && this.inputRef) {
                    this.isMouseIn = false;
                    this.inputRef.focus();
                }
            }
        );
    }

    private addSeletedItem(): void {
        let item = this.state.autoCompleteItemsMatches.filter(x => x.Active == true);
        if (item.count() > 0) {
            this.selectItem(item.get(0));
        }
    }

    private filterAsInteger(input: string): string {
        if (!input)
            return null;

        return input.replace(/[^\d]/g, "");
    }
}

export { ITextboxProps, TextboxControl };
